import type { ChapterComment } from "./models";

export type VariationChapterComment = {
  type?: string;
  text: string;
  targetText?: string;
};

/** Story Craft inline comments (`createdBy: ai`). */
export function isStoryCraftComment(comment: ChapterComment): boolean {
  return comment.createdBy === "ai";
}

export function withoutStoryCraftComments(comments: ChapterComment[]): ChapterComment[] {
  return comments.filter((comment) => !isStoryCraftComment(comment));
}

export function storyCraftComments(comments: ChapterComment[]): ChapterComment[] {
  return comments.filter((comment) => isStoryCraftComment(comment));
}

export function unresolvedComments(comments: ChapterComment[]): ChapterComment[] {
  return comments.filter((comment) => !comment.resolved);
}

/** Unresolved chapter comments serialized for variation generation. */
export function unresolvedCommentsForVariation(comments: ChapterComment[]): VariationChapterComment[] {
  return unresolvedComments(comments)
    .filter((comment) => comment.text.trim().length > 0)
    .map((comment) => ({
      type: comment.type,
      text: comment.text.trim(),
      targetText: comment.targetText?.trim() || undefined,
    }));
}

export function formatUnresolvedCommentsForVariationPrompt(comments: VariationChapterComment[]): string {
  if (!comments.length) return "";

  const lines = comments.map((comment) => {
    const loc = comment.targetText
      ? ` (on: "${comment.targetText.length > 60 ? `${comment.targetText.slice(0, 57)}...` : comment.targetText}")`
      : "";
    const typeLabel = comment.type ? `[${comment.type}]` : "[comment]";
    return `• ${typeLabel}${loc}: ${comment.text}`;
  });

  return [
    "=== CHAPTER COMMENTS (address these in your revision) ===",
    "The author/editor left these unresolved comments on the chapter. Incorporate the feedback where relevant:",
    ...lines,
  ].join("\n");
}
